import React, { useState, useRef, forwardRef } from 'react';
import stopEvent from '@tmap/mmm-style-guide/src/util/stopEvent';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Dialog } from '@tmap/mmm-style-guide/src/Dialog';
import { AppBar } from '@tmap/mmm-style-guide/src/AppBar';
import { Close } from '@tmap/mmm-style-guide/src/Icon';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Tooltip } from '@tmap/mmm-style-guide/src/Tooltip';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Toolbar } from '@tmap/mmm-style-guide/src/Toolbar';
import { Slide } from '@tmap/mmm-style-guide/src/Transition';

import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Navigation, Pagination, Thumbs, Zoom,
} from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/zoom';

import { VideoFull, VideoThumb } from './video';
import { ImageFull, ImageThumb } from './sanityImage';
import SingleAspectImage from './coverImages/singleAspect';
import MultiImages from './coverImages/multi';
import SingleSlantImage from './coverImages/singleSlant';
import useSwipeHandlers from '../../hooks/useSwipeHandlers';

const MaxsizeGallery = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  overflow: 'hidden',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1,
  background: theme.palette.grey[900],
  '& .swiper': {
    width: '100%',
  },
  '& .swiper-button-next, & .swiper-button-prev': {
    color: theme.palette.common.white,
    [theme.breakpoints.down('md')]: {
      visibility: 'hidden',
    },
  },
  '& .swiper-slide': {
    maxWidth: '100vw',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const MinsizeGallery = styled('div')(({ theme }) => ({
  position: 'relative',
  flexGrow: 0,
  flexShrink: 0,
  '& .swiper-slide': {
    width: 'auto',
    height: 'auto',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
    marginLeft: theme.spacing(1.5),
    '&:last-of-type': {
      marginRight: theme.spacing(1.5),
    },
    // selected indicator
    '&::after': {
      position: 'absolute',
      zIndex: 2,
      content: '""',
      background: theme.palette.primary.veryDark,
      left: '10%',
      width: '80%',
      borderRadius: '8px',
      height: theme.spacing(3),
      bottom: theme.spacing(-6),
      transition: `bottom ${theme.transitions.duration.short}ms ${theme.transitions.easing.sharp}`,
    },
    '&.swiper-slide-thumb-active::after': {
      bottom: theme.spacing(-3.5),
    },
    // shrink animation on thumb content
    '&.swiper-slide-thumb-active>div': {
      transform: 'scale(0.9)',
    },
  },
}));

const GalleryThumbContentWrapper = styled('div')(({ theme }) => ({
  // rounded corner cutouts
  width: 'auto',
  height: '100%',
  overflow: 'hidden',
  borderRadius: '8px',
  // shrink animation
  transform: 'scale(1)',
  transition: `transform ${theme.transitions.duration.short}ms ${theme.transitions.easing.sharp}`,
}));

const Transition = forwardRef((props, ref) => <Slide direction='down' ref={ref} {...props} />);

const CoverImageContainer = styled(Grid)(({ theme }) => ({
  '.react-player__preview': {
    [theme.breakpoints.up('sm')]: {
      borderRadius: '8px',
    },
  },
}));

function FullscreenGalleryDialog(props) {
  const {
    open, handleClose, slides = [], title,
  } = props;

  const [current, setCurrent] = useState('');
  const [total, setTotal] = useState('');

  const swiperRef = useRef(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);

  const touchHandlers = useSwipeHandlers({
    onSwipeUp: handleClose,
  });

  // swiper calls this function to obtain HTML for its pager.
  // so we provide it with none and perform our own effects.
  const renderCustomSwiperPager = (swiper, sCurrent, sTotal) => {
    setCurrent(sCurrent);
    setTotal(sTotal);
    return '';
  };

  // autoplay the first gallery item if it is a video
  const autoplay = open && (swiperRef?.current?.swiper?.activeIndex === 0);

  return (
    <Dialog
      fullScreen
      open={open || false}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted={open !== undefined}
      {...touchHandlers}
    >
      <AppBar position='relative' sx={(theme) => ({ background: theme.palette.primary.veryDark })}>
        <Toolbar>
          <Grid container alignItems='center' spacing={2} flexWrap='nowrap'>
            <Grid item xs>
              <Grid container alignItems='center' spacing={2}>
                <Grid item>
                  <Typography variant='h6'>{title}</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant='body1'>{current && total && <>{current}&nbsp;/&nbsp;{total}</>}</Typography>
            </Grid>
            <Grid item>
              <Tooltip title='Close' arial-label='close gallery'>
                <span><IconButton color='inherit' onClick={handleClose} aria-label='close'><Close /></IconButton></span>
              </Tooltip>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <MaxsizeGallery>
        <Swiper
          ref={swiperRef}
          modules={[Navigation, Pagination, Thumbs, Zoom]}
          navigation
          pagination={{ type: 'custom', renderCustom: renderCustomSwiperPager }}
          thumbs={{ swiper: thumbsSwiper, autoScrollOffset: 1 }}
          zoom
          spaceBetween={12}
          slidesPerView='auto'
        >
          {slides.map(({ fullComponent: FullComponent, props: slideProps }, i) => (
            <SwiperSlide key={`gallery-full-${i}`}>
              <FullComponent {...slideProps} playing={i === 0 && autoplay} />
            </SwiperSlide>
          ))}
        </Swiper>
      </MaxsizeGallery>
      <MinsizeGallery>
        <Swiper
          modules={[Thumbs]}
          onSwiper={setThumbsSwiper}
          slidesPerView='auto'
        >
          {slides.map(({ thumbComponent: ThumbComponent, props: slideProps }, i) => (
            <SwiperSlide key={`gallery-thumb-${i}`}>
              <GalleryThumbContentWrapper>
                <ThumbComponent {...slideProps} />
              </GalleryThumbContentWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
      </MinsizeGallery>
    </Dialog>
  );
}

function CoverImage(props) {
  const {
    mode,
    mediaList,
    mediaComponents,
    aspectRatio,
  } = props;
  const imagesOnly = mediaList.filter((media) => media._type === 'standardImage');
  if (mode === 'slant') {
    return (
      <SingleSlantImage image={imagesOnly[0]} totalImageCount={mediaComponents.length} />
    );
  }
  if (mode === 'multi' && mediaList.length >= 3) {
    return (
      <MultiImages images={mediaList} />
    );
  }
  return (
    <SingleAspectImage
      media={mediaList[0]}
      totalImageCount={mediaComponents.length}
      aspectRatio={aspectRatio}
    />
  );
}

function SanityGallery(props) {
  const {
    mediaList = [],
    mode = 'single',
    title,
    aspectRatio,
  } = props;
  // begins at undefined to lazy-load gallery
  // does not unmount swiper when set to false to avoid bugs
  const [open, setOpen] = useState(undefined);

  const handleClickOpen = (e) => {
    stopEvent(e);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const mediaComponents = mediaList.map((media) => {
    switch (media._type) {
      case 'standardImage':
        return {
          fullComponent: ImageFull,
          thumbComponent: ImageThumb,
          props: { image: media },
        };
      case 'videoBlock':
        return {
          fullComponent: VideoFull,
          thumbComponent: VideoThumb,
          props: { url: media.url, caption: media.caption },
        };
      default:
        return null;
    }
  }).filter((media) => media);

  return (
    <>
      <FullscreenGalleryDialog
        open={open}
        handleClose={handleClose}
        slides={mediaComponents}
        title={title}
      />
      <CoverImageContainer container spacing={{ xs: 0, sm: 2 }} onClickCapture={handleClickOpen} sx={{ cursor: 'zoom-in' }}>
        <CoverImage
          mode={mode}
          mediaList={mediaList}
          mediaComponents={mediaComponents}
          aspectRatio={aspectRatio}
        />
      </CoverImageContainer>
    </>
  );
}

export default SanityGallery;
